import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { 
  Download, 
  Table as TableIcon
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatPercent = (value) => {
  return value.toFixed(2) + '%';
};

export const PaymentTable = ({ result }) => {
  const [showCorrected, setShowCorrected] = useState(true);

  // Move hooks before any conditional returns
  const filteredData = useMemo(() => {
    if (!result?.projecoes?.length) return [];
    return [...result.projecoes];
  }, [result?.projecoes]);

  // Display all data with scrolling
  const displayData = useMemo(() => {
    return filteredData;
  }, [filteredData]);

  if (!result || !result.projecoes || result.projecoes.length === 0) {
    return null;
  }

  const handleExportCSV = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Cronograma');

    const headers = [
      'Prazo', 'Data', 'Parcela', 'Reforço', 'Total Investido',
      'Índice', 'Correção', 'Saldo Devedor', 'Valorização',
      'Ganho Capital', '% s/ C.I.', 'Ganho Real', 'Lucro Líquido'
    ];

    // Column color mapping (1-indexed): 8=Saldo Devedor(red), 9=Valorização(blue), 11=%CI(green), 12=Ganho Real(green), 13=Lucro Líquido(dark blue)
    const colColors = { 8: 'CC0000', 9: '2563EB', 11: '059669', 12: '059669', 13: '1D4ED8' };

    // Header row
    const headerRow = ws.addRow(headers);
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, color: { argb: colColors[colNumber] ? 'FF' + colColors[colNumber] : 'FF000000' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.alignment = { horizontal: colNumber >= 3 ? 'right' : 'left' };
    });

    // Data rows
    result.projecoes.forEach(p => {
      const row = ws.addRow([
        p.mes,
        p.data,
        p.parcela_corrigida,
        p.reforco_corrigido > 0 ? p.reforco_corrigido : null,
        p.total_investido_corrigido,
        p.indice_aplicado || '-',
        p.correcao_percentual / 100,
        p.saldo_devedor,
        p.valor_imovel,
        p.ganho_capital_acumulado,
        p.percentual_sobre_ci / 100,
        p.ganho_real,
        p.lucro_liquido
      ]);
      row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
        if (colColors[colNumber]) {
          cell.font = { color: { argb: 'FF' + colColors[colNumber] }, bold: colNumber === 13 };
        }
        if (colNumber >= 3 && colNumber !== 6 && colNumber !== 7 && colNumber !== 11) {
          cell.numFmt = '#,##0.00;[Red]-#,##0.00';
        }
        if (colNumber === 7 || colNumber === 11) {
          cell.numFmt = '0.00%';
        }
        cell.alignment = { horizontal: colNumber >= 3 ? 'right' : 'left' };
      });
    });

    // Auto-width columns
    ws.columns.forEach((col, i) => {
      col.width = i < 2 ? 10 : 18;
    });

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), 'projecao_investimento.xlsx');
  };

  return (
    <Card className="bg-white border-slate-200 animate-fade-in">
      <CardHeader className="pb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
            <TableIcon className="text-emerald-600" size={20} />
            Cronograma de Pagamentos
          </CardTitle>
          
          <div className="flex items-center gap-3">
            {/* Toggle Corrected Values */}
            <Button
              data-testid="toggle-corrected"
              variant={showCorrected ? "default" : "outline"}
              size="sm"
              onClick={() => setShowCorrected(!showCorrected)}
              className="text-xs"
            >
              {showCorrected ? 'Com Correção' : 'Sem Correção'}
            </Button>
            
            {/* Export Button */}
            <Button
              data-testid="btn-export-csv"
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Exportar Excel
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative border border-slate-200 rounded-lg">
          <ScrollArea className="h-[380px] w-full">
            <div className="min-w-[1300px] pr-4">
              <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="whitespace-nowrap">Prazo</TableHead>
                  <TableHead className="whitespace-nowrap">Data</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Parcela</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Reforço</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Total Investido</TableHead>
                  <TableHead className="whitespace-nowrap text-center">Índice</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Correção</TableHead>
                  <TableHead className="whitespace-nowrap text-right text-red-600">Saldo Devedor</TableHead>
                  <TableHead className="whitespace-nowrap text-right text-blue-600">Valorização</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Ganho Capital</TableHead>
                  <TableHead className="whitespace-nowrap text-right">% s/ C.I.</TableHead>
                  <TableHead className="whitespace-nowrap text-right text-emerald-600">Ganho Real</TableHead>
                  <TableHead className="whitespace-nowrap text-right text-primary font-bold">Lucro Líquido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData.map((row) => (
                  <TableRow 
                    key={row.mes} 
                    className={`table-row ${row.reforco_corrigido > 0 ? 'bg-amber-50/50' : ''} ${row.mes === 36 ? 'bg-emerald-50 border-primary/30 border-2' : ''}`}
                  >
                    <TableCell className="font-medium">{row.mes}</TableCell>
                    <TableCell className="whitespace-nowrap">{row.data}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(showCorrected ? row.parcela_corrigida : row.parcela_original)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.reforco_corrigido > 0 
                        ? formatCurrency(showCorrected ? row.reforco_corrigido : row.reforco_original) 
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(showCorrected ? row.total_investido_corrigido : row.total_investido_original)}
                    </TableCell>
                    <TableCell className="text-center text-xs">
                      {row.indice_aplicado ? (
                        <span className={`px-2 py-0.5 rounded font-semibold ${
                          (row.indice_aplicado || '').toUpperCase().startsWith('CUB') || (row.indice_aplicado || '').toUpperCase().startsWith('INCC')
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {row.indice_aplicado}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-amber-600">
                      {formatPercent(row.correcao_percentual)}
                    </TableCell>
                    <TableCell className={`text-right font-mono text-sm text-red-600 ${row.saldo_devedor < 0 ? 'text-emerald-600' : ''}`}>
                      {formatCurrency(row.saldo_devedor)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-blue-600 font-semibold">
                      {formatCurrency(row.valor_imovel)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(row.ganho_capital_acumulado)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-emerald-600">
                      {formatPercent(row.percentual_sobre_ci)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-emerald-600 font-semibold">
                      {formatCurrency(row.ganho_real)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-primary font-bold">
                      {formatCurrency(row.lucro_liquido)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
        
        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500 space-y-1">
          <p><strong>Índice</strong> = Indexador aplicado no mês (<span className="text-amber-700">CUB/INCC durante obra</span> · <span className="text-blue-700">IPCA/IGP-M pós-obra</span>)</p>
          <p><strong>Ganho Real</strong> = Valorização Prevista - Saldo Devedor</p>
          <p><strong>Lucro Líquido</strong> = Ganho Real - Total Investido</p>
          <p><strong>% sobre C.I.</strong> = Valorização do Capital Investido</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentTable;
